import { useState, useEffect, useRef } from 'react';

interface CountUpProps {
  start?: number;
  end: number;
  duration?: number;
  locale?: string;
}

/**
 * A custom React hook to animate a count up to a target number with specific formatting.
 * @param {CountUpProps} props - The configuration for the count-up animation.
 * @returns {string} The current formatted value as a string.
 */
const CountUp = ({
  start = 0,
  end,
  duration = 1000,
  locale = 'en-US',
}: CountUpProps): string => {
  // Use a state variable to track the value that's being animated.
  // We initialize it to the 'start' prop.
  const [currentValue, setCurrentValue] = useState(start);
  
  // Refs for animation loop management and timing.
  const requestRef = useRef<number>();
  const startTimeRef = useRef<number>();

  const animateCount = (timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const progress = timestamp - startTimeRef.current;

    // Calculate how far along the duration we are (0 to 1)
    const timeProgress = Math.min(progress / duration, 1);
    
    // Linearly interpolate between 'start' and 'end' based on time progress.
    const nextValue = start + (end - start) * timeProgress;

    // We use Math.floor() to keep the animated value an integer.
    setCurrentValue(Math.floor(nextValue));

    if (progress < duration) {
      requestRef.current = requestAnimationFrame(animateCount);
    } else {
      // Ensure the final value is exactly the 'end' prop
      setCurrentValue(end);
    }
  };

  useEffect(() => {
    // 1. **CRITICAL FIX**: Reset internal state before starting a new animation.
    // When 'end' changes, we want the animation to start from 'start' again.
    startTimeRef.current = undefined;
    setCurrentValue(start); // Start the count from the 'start' prop

    // 2. Start the new animation frame loop.
    requestRef.current = requestAnimationFrame(animateCount);

    // 3. Clean-up function: This runs when the component unmounts OR 
    // when 'end', 'duration', or 'start' changes (due to the dependency array),
    // stopping the currently running animation before starting a new one.
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [end, duration, start]); // Re-run effect whenever these props change

  // Format the number using toLocaleString() for output
  return currentValue.toLocaleString(locale);
};

export default CountUp;